// frontend/src/components/search/SearchBar.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Icon from '../common/Icon';
import useDebounce from '../../hooks/useDebounce';
import Modal from '../common/Modal'; // NEW: Import Modal
import SearchModalContent from './SearchModalContent'; // NEW: Import SearchModalContent
import './SearchBar.css';

/**
 * A reusable SearchBar component for direct search functionality,
 * or as a trigger for a full-screen global search modal.
 * @param {object} props
 * @param {string} [props.initialQuery=''] - Initial value for the search input.
 * @param {function} [props.onSearchSubmit] - Callback when search is submitted (in non-overlay mode): (searchTerm) => void.
 *                                          If not provided, defaults to navigating to /search?q=searchTerm.
 * @param {string} [props.placeholder='Search...'] - Placeholder text for the input.
 * @param {boolean} [props.autoSubmitOnDebounce=false] - If true, submits search after debounce delay (in non-overlay mode).
 * @param {number} [props.debounceDelay=500] - Delay in ms for auto-submission.
 * @param {boolean} [props.showSubmitButton=true] - Whether to show a submit button (in non-overlay mode).
 * @param {string} [props.className=''] - Additional CSS class for the form container.
 * @param {'small'|'medium'|'large'} [props.size='medium'] - Visual size of the search bar.
 * @param {boolean} [props.isGlobalTrigger=false] - If true, clicking this search bar opens the Global Search Modal.
 */
const SearchBar = ({
  initialQuery = '',
  onSearchSubmit,
  placeholder = 'Search Z...',
  autoSubmitOnDebounce = false,
  debounceDelay = 500,
  showSubmitButton = true,
  className = '',
  size = 'medium',
  isGlobalTrigger = false // NEW: Prop to control trigger mode
}) => {
  const [searchTerm, setSearchTerm] = useState(initialQuery);
  const debouncedSearchTerm = useDebounce(searchTerm, debounceDelay);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inputRef = useRef(null);

  // NEW: State to control the visibility of the global search modal
  const [showSearchModal, setShowSearchModal] = useState(false);

  // Effect to update searchTerm if initialQuery from URL changes (relevant for non-trigger mode)
  useEffect(() => {
    if (!isGlobalTrigger) {
      const queryFromUrl = searchParams.get('q');
      if (queryFromUrl && queryFromUrl !== searchTerm && window.location.pathname.includes('/search')) {
        setSearchTerm(queryFromUrl);
      }
    }
  }, [searchParams, searchTerm, isGlobalTrigger]);


  const handleSubmit = useCallback((e) => {
    if (e) e.preventDefault(); // Prevent form default submission

    if (isGlobalTrigger) {
      // If it's a global trigger, just open the modal
      setShowSearchModal(true);
      return;
    }

    // Standard search bar behavior below (only if not a global trigger)
    const termToSubmit = searchTerm.trim();
    if (!termToSubmit) return;

    if (typeof onSearchSubmit === 'function') {
      onSearchSubmit(termToSubmit);
    } else {
      navigate(`/search?q=${encodeURIComponent(termToSubmit)}`);
    }
  }, [searchTerm, onSearchSubmit, navigate, isGlobalTrigger]);

  // Effect for auto-submission on debounced search term change (only in non-trigger mode)
  useEffect(() => {
    if (!isGlobalTrigger && autoSubmitOnDebounce && debouncedSearchTerm.trim() && debouncedSearchTerm !== initialQuery) {
        const currentQueryParam = searchParams.get('q');
        if (debouncedSearchTerm === currentQueryParam) return;
        
        handleSubmit();
    }
  }, [debouncedSearchTerm, autoSubmitOnDebounce, initialQuery, handleSubmit, isGlobalTrigger, searchParams]);


  const handleClearSearch = () => {
    setSearchTerm('');
    inputRef.current?.focus();
    if (!isGlobalTrigger) { // Only trigger parent/navigation if not in global trigger mode
      if (typeof onSearchSubmit === 'function') {
          onSearchSubmit('');
      } else if (window.location.pathname.includes('/search')) {
          navigate(window.location.pathname);
      }
    }
  };

  const handleCloseModal = useCallback(() => {
    setShowSearchModal(false);
    setSearchTerm(''); // Clear search term when modal closes
  }, []);

  return (
    <>
      <form 
        onSubmit={handleSubmit} 
        className={`search-bar-form search-bar-size-${size} ${className}`} 
        role="search"
        // If it's a global trigger, onClick handles opening the modal
        // The onSubmit also calls handleSubmit, which will open the modal if isGlobalTrigger.
        // This makes the form element itself clickable as a single unit.
        onClick={isGlobalTrigger ? handleSubmit : undefined}
      >
        <div className="search-input-wrapper">
          {(!showSubmitButton || isGlobalTrigger) && ( // Show icon if no submit button, or always if global trigger
              <Icon name="search" className="search-input-icon-leading" aria-hidden="true" />
          )}
          <input
            ref={inputRef}
            type="search"
            name="q"
            placeholder={placeholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input-field"
            aria-label="Search content"
            readOnly={isGlobalTrigger} // Input is read-only if it's a global trigger
            tabIndex={isGlobalTrigger ? -1 : 0} // Prevent tabbing into it if read-only
          />
          {searchTerm && !isGlobalTrigger && ( // Show clear button only if text AND not global trigger
            <button 
              type="button" 
              onClick={handleClearSearch} 
              className="search-clear-button"
              aria-label="Clear search input"
            >
              <Icon name="close" size={size === 'small' ? 16 : 18} />
            </button>
          )}
        </div>
        {showSubmitButton && !isGlobalTrigger && ( // Show submit button only if not global trigger
          <button type="submit" className="search-submit-button" aria-label="Submit search">
              <Icon name="search" size={size === 'small' ? 18 : 20} />
          </button>
        )}
      </form>

      {/* NEW: Conditionally render the Modal with SearchModalContent */}
      {showSearchModal && (
        <Modal
          isOpen={showSearchModal}
          onClose={handleCloseModal}
          isFullScreen={true} // Assuming your Modal component supports a full-screen mode
          backdropDismiss={true}
        >
          <SearchModalContent onClose={handleCloseModal} />
        </Modal>
      )}
    </>
  );
};

export default SearchBar;